import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Skeleton, Switch, Tag, message } from 'antd';
import dayjs from 'dayjs';
import {
  getEinvoiceReminderSettings,
  saveEinvoiceReminderSettings,
} from '@/services/einvoiceReminders.service';
import EinvoiceReminderPreviewModal from './EinvoiceReminderPreviewModal';
import EinvoiceSummarySettings from './EinvoiceSummarySettings';

const fmt = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '—');

/**
 * Global switch of the monthly e-invoice reminder, inside the team settings
 * (where the "always in copy" list of automatic client mailings already is).
 * Saved on its own, not with the settings form: turning sending on is a
 * separate decision and asks for confirmation.
 */
export default function EinvoiceReminderSettingsSection() {
  const { t } = useTranslation('einvoiceReminders');
  const queryClient = useQueryClient();
  const [previewOpen, setPreviewOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['einvoice-reminder-settings'],
    queryFn: getEinvoiceReminderSettings,
  });

  const save = useMutation({
    mutationFn: (enabled) => saveEinvoiceReminderSettings({ enabled }),
    onSuccess: (d) => {
      queryClient.setQueryData(['einvoice-reminder-settings'], d);
      message.success(
        d.enabled ? t('settings.turnedOn') : t('settings.turnedOff')
      );
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const toggle = (enabled) => {
    if (!enabled) return save.mutate(false);
    Modal.confirm({
      title: t('settings.confirmTitle'),
      content: t('settings.confirmText', { count: data?.companiesOn || 0 }),
      okText: t('settings.confirmOk'),
      cancelText: t('settings.cancel'),
      okButtonProps: { shape: 'round' },
      cancelButtonProps: { shape: 'round' },
      onOk: () => save.mutateAsync(true),
    });
    return undefined;
  };

  if (isLoading || !data) return <Skeleton active paragraph={{ rows: 2 }} />;

  return (
    <div className="grid gap-3">
      <div className="grid gap-2 rounded-lg border border-zinc-200 p-3">
        <div className="flex items-center justify-between gap-3">
          <b>{t('settings.title')}</b>
          <Switch
            checked={data.enabled}
            disabled={!data.canManage}
            loading={save.isPending}
            onChange={toggle}
          />
        </div>
        <div className="text-xs opacity-70">{t('settings.rule')}</div>
        <div className="flex flex-wrap items-center gap-1 text-xs">
          <span className="opacity-70">{t('settings.schedule')}:</span>
          {data.schedule.map((d) => (
            <Tag
              key={d.date}
              color={d.date === data.nextSendDay?.date ? 'blue' : undefined}
              className="m-0"
            >
              {fmt(d.date)}
            </Tag>
          ))}
        </div>
        <div className="text-xs">
          {data.enabled ? t('settings.stateOn') : t('settings.stateOff')} ·{' '}
          {t('settings.companiesOn', { count: data.companiesOn })}
        </div>
        <div className="flex justify-end">
          <Button
            size="small"
            shape="round"
            icon={<EyeOutlined />}
            onClick={() => setPreviewOpen(true)}
            disabled={!data.canManage}
          >
            {t('settings.preview')}
          </Button>
        </div>
        <EinvoiceReminderPreviewModal
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      </div>
      <EinvoiceSummarySettings data={data} />
    </div>
  );
}
